import { Injectable, computed, inject, signal } from '@angular/core'
import { ClassManagerApiService } from '../../_services/system_api/classManager/api.service'
import { FuseqSessionService } from '../../FuseQ/session.service'

export interface NavFavorite {
  name: string
  url: string
  icon: string
  parent?: string
}

interface NavNode {
  name?: string
  url?: string
  icon?: string
  children?: NavNode[]
  [key: string]: any
}

const STORAGE_PREFIX = 'awp.nav.favorites.v1.'
const MAX_FAVORITES = 8
const REMOTE_SAVE_DEBOUNCE_MS = 700
const OPTIONS_KEY = 'navFavorites'

function normalizeUrl (url: string): string {
  return (url || '').split('?')[0].split('#')[0].replace(/\/+$/, '').toLowerCase()
}

function sanitizeFavorites (value: any): string[] {
  return Array.isArray(value)
    ? value.filter((item) => typeof item === 'string').slice(0, MAX_FAVORITES)
    : []
}

/**
 * Navigazione e preferiti (pin) del menu laterale.
 *
 * I preferiti sono salvati in `System_Users.Options.navFavorites` (sync tra
 * dispositivi) tramite l'endpoint esistente `recordManager/updateRecordOptions`
 * con `merge: true`. localStorage resta come cache/fallback offline e per la
 * migrazione dei preferiti creati prima del sync server.
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly classApi = inject(ClassManagerApiService)
  private readonly session = inject(FuseqSessionService)

  private username = ''
  private userId: string | number | null = null
  private saveTimer: ReturnType<typeof setTimeout> | null = null

  /** Albero menu effettivo (permessi + catalog dinamico), impostato dal layout. */
  readonly navTree = signal<NavNode[]>([])

  /** URL normalizzati dei preferiti, nell'ordine scelto dall'utente. */
  readonly favorites = signal<string[]>([])

  /** url normalizzato -> voce di menu. */
  readonly flatNav = computed(() => {
    const map = new Map<string, NavFavorite>()
    const walk = (items: NavNode[], parent?: string) => {
      for (const item of items || []) {
        const hasChildren = !!item.children && item.children.length > 0
        if (item.url && item.name && !hasChildren) {
          const key = normalizeUrl(item.url)
          if (key && !map.has(key)) {
            map.set(key, { name: item.name, url: item.url, icon: item.icon || '', parent })
          }
        }
        if (hasChildren) {
          walk(item.children as NavNode[], item.name)
        }
      }
    }
    walk(this.navTree())
    return map
  })

  /** Preferiti risolti sull'albero corrente: le voci non più visibili sono omesse. */
  readonly favoriteItems = computed<NavFavorite[]>(() => {
    const flat = this.flatNav()
    return this.favorites()
      .map((url) => flat.get(url))
      .filter((item): item is NavFavorite => !!item)
  })

  readonly maxFavorites = MAX_FAVORITES

  /**
   * Inizializza i preferiti con quelli server (Options del login) o, se il
   * server non li ha ancora, con la cache locale (migrazione automatica).
   */
  init (username: string, userId?: string | number | null, options?: any): void {
    const next = username || 'anon'
    if (next === this.username) {
      return
    }
    this.username = next
    this.userId = userId ?? null

    const serverFavorites = Array.isArray(options?.[OPTIONS_KEY]) ? sanitizeFavorites(options[OPTIONS_KEY]) : null
    if (serverFavorites !== null) {
      this.favorites.set(serverFavorites)
      this.persistLocal()
    } else {
      const local = this.readLocal()
      this.favorites.set(local)
      if (local.length > 0) {
        this.scheduleRemoteSave()
      }
    }
  }

  setTree (tree: NavNode[]): void {
    this.navTree.set(tree || [])
  }

  isFavorite (url: string): boolean {
    return this.favorites().includes(normalizeUrl(url))
  }

  canPin (item: NavNode): boolean {
    return !!item?.url && (!item.children || item.children.length === 0) && this.flatNav().has(normalizeUrl(item.url))
  }

  /**
   * URL della voce di menu che punta a una classe del catalog. Le voci
   * dinamiche hanno forma `.../c/<classname>/<label>`: la pagina Catalog non
   * deve ricostruire l'URL, altrimenti il pin diverge dalla sidebar quando
   * cambia la descrizione della classe.
   *
   * `baseUrl` disambigua quando la stessa classe è generata in più catalog
   * (es. `/admin/catalog/c/` e `/catalog/c/`): si preferisce la voce che
   * appartiene al catalog da cui si sta pinnando.
   */
  private readonly classNavUrls = computed(() => {
    const map = new Map<string, { url: string, key: string }[]>()
    for (const [key, item] of this.flatNav()) {
      const match = /\/c\/([^/]+)\//i.exec(item.url || '')
      if (!match) {
        continue
      }
      const classname = match[1].toLowerCase()
      const list = map.get(classname) ?? []
      list.push({ url: item.url, key })
      map.set(classname, list)
    }
    return map
  })

  urlForClassname (classname: string, baseUrl?: string): string | null {
    if (!classname) {
      return null
    }
    const candidates = this.classNavUrls().get(classname.toLowerCase())
    if (!candidates || candidates.length === 0) {
      return null
    }
    const base = normalizeUrl(baseUrl || '')
    if (base) {
      const match = candidates.find((c) => c.key.startsWith(base))
      if (match) {
        return match.url
      }
    }
    return candidates[0].url
  }

  /** Aggiunge/rimuove un preferito. 'limit' quando si è raggiunto il massimo. */
  toggle (url: string): 'added' | 'removed' | 'limit' {
    const key = normalizeUrl(url)
    if (!key) {
      return 'removed'
    }
    if (this.favorites().includes(key)) {
      this.favorites.update((list) => list.filter((item) => item !== key))
      this.onFavoritesChanged()
      return 'removed'
    }
    if (this.favorites().length >= MAX_FAVORITES) {
      return 'limit'
    }
    this.favorites.update((list) => [...list, key])
    this.onFavoritesChanged()
    return 'added'
  }

  remove (url: string): void {
    const key = normalizeUrl(url)
    this.favorites.update((list) => list.filter((item) => item !== key))
    this.onFavoritesChanged()
  }

  private onFavoritesChanged (): void {
    this.persistLocal()
    this.syncSession()
    this.scheduleRemoteSave()
  }

  private scheduleRemoteSave (): void {
    if (!this.userId) {
      return
    }
    if (this.saveTimer) {
      clearTimeout(this.saveTimer)
    }
    this.saveTimer = setTimeout(() => this.saveRemote(), REMOTE_SAVE_DEBOUNCE_MS)
  }

  private saveRemote (): void {
    this.saveTimer = null
    if (!this.userId) {
      return
    }
    this.classApi.updateRecordOptions({
      classname: 'System_Users',
      id: this.userId,
      merge: true,
      options: { [OPTIONS_KEY]: this.favorites() }
    }).subscribe({
      next: () => this.syncSession(),
      error: () => { /* offline/permessi: resta la cache locale */ }
    })
  }

  /** Mantiene allineata la copia di sessione (usata al prossimo login/refresh). */
  private syncSession (): void {
    const current = this.session.session
    if (!current) {
      return
    }
    const options = { ...(current.options || {}), [OPTIONS_KEY]: this.favorites() }
    this.session.session = { ...current, options }
  }

  private storageKey (): string {
    return `${STORAGE_PREFIX}${this.username || 'anon'}`
  }

  private readLocal (): string[] {
    try {
      const raw = localStorage.getItem(this.storageKey())
      return sanitizeFavorites(raw ? JSON.parse(raw) : [])
    } catch {
      return []
    }
  }

  private persistLocal (): void {
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(this.favorites()))
    } catch { /* storage non disponibile */ }
  }
}
